/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useEffect, useState} from 'react';
import {Spinner} from 'react-bootstrap';
import toast from 'react-hot-toast';

import {usePostRequest} from '../../../../../api/helper';
import {setIsEditStudyDialogOpen} from '../../../../../redux/ducks/CoverageRequirements/slice';
import {useAppDispatch, useAppSelector} from '../../../../../redux/hooks';
import store from '../../../../../redux/store';
import ArgosButton from '../../../../components/Buttons/ArgosButton';
import ArgosDialogHeader from '../../../../components/Dialogs/ArgosDialogHeader';
import ArgosMenuItem from '../../../../components/Form/Select/ArgosMenuItem';
import ArgosSelect from '../../../../components/Form/Select/ArgosSelect';
import ArgosTextField from '../../../../components/Form/TextField/ArgosTextField';
import SeparatorLine from '../../../../components/Separator/SeparatorLine';
import {checkPlanOfRecordEndpoint, usersDataEndpoint} from '../../constants';
import {Container, fieldHeight, fieldWidth, Header, LoaderWrapper, User} from './constants';
import {getIsUserAdmin, getRouteKeyAccessLevel, useHasWriteAccess} from "../../../../../redux/ducks/AccessControl/accessControlHelpers";
import {EUserPermissionAccess, IUser} from "../../../../../redux/ducks/AccessControl/types";
import {Checkbox, FormControl, IconButton, InputLabel} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArgosFlexbox from '../../../../components/Containers/ArgosFlexbox';
import FormControlLabel from "@mui/material/FormControlLabel";
import {selectCurrentUser} from "../../../../../redux/ducks/AccessControl/slice";
import {allowAdminOrOwnerPermissions, determineSaveState, planOfRecordDisablePermissions} from '../../../../../helpers/permissionHelpers';
import { EArgosRouteKeys } from '../../../../../Routes/constants';
import ArgosDialog from '../../../../components/Dialogs/ArgosDialog';
import BoxWithCenteredContent from '../../../../components/Containers/BoxWithCenteredContent';

function convertUTCDateToLocalDate(date: any) {
  var newDate = date; //We are already in UTC
  var offset = date.getTimezoneOffset() / 60; //offset in hours
  var hours = date.getHours();

  newDate.setHours(hours - offset);

  return newDate;
}

/**
 * Handles the checkbox state change for the Plan of Record and Archive options in the Edit Study dialog.
 * If the Plan of Record checkbox is unchecked, it checks if the study is linked to any ADAS programs that are Plan of Record.
 * @param e - event object containing the checked state of the checkbox
 * @param prevStudyPlanOfRecord 
 * @param setIsProcessing 
 * @param setOpenDialog 
 * @param fetchLinkedHandle 
 * @param studyId 
 * @param setLinkedData 
 * @param setIsPlanOfRecord 
 */
export const handleCheck = async (
  e: { target: { checked: boolean } },
  prevStudyPlanOfRecord: boolean,
  setIsProcessing: React.Dispatch<React.SetStateAction<boolean>>,
  setOpenDialog: React.Dispatch<React.SetStateAction<boolean>>,
  fetchLinkedHandle: (body: any) => Promise<any>,
  studyId: string | undefined,
  setLinkedData: React.Dispatch<React.SetStateAction<Record<string, any>>>,
  setIsPlanOfRecord: React.Dispatch<React.SetStateAction<boolean>>
): Promise<void> => {
  const newStudyPlanOfRecord = e.target.checked;
  let allowSetPlanOfRecord = true;

  if (prevStudyPlanOfRecord && newStudyPlanOfRecord === false) {
    allowSetPlanOfRecord = false;
    setIsProcessing(true);
    setOpenDialog(true);

    fetchLinkedHandle({ studyId })
      .then((data: any) => {
        setLinkedData(data);
        setIsProcessing(false);
      })
      .catch((err) => {
        console.log(err);
        toast.error("Failed to process linked ADAS programs for this study");
        setIsProcessing(false);
        setOpenDialog(false);
      });
  }

  if (allowSetPlanOfRecord) {
    setIsPlanOfRecord(e.target.checked);
  }
};
/**
 *  Renders a dialog to check if the coverage analysis study is linked to one or more ADAS programs that are Plan of Record (PoR).
 *  Displays a loading spinner while checking, and shows the linked ADAS programs if any.
 * @param openDialog 
 * @param setOpenDialog 
 * @param isProcessing 
 * @param linkedData 
 * @param setLinkedData 
 * @param setIsPlanOfRecord 
 * @returns  JSX.Element
 */
export const renderCheckAdasLinkDialog = (
  openDialog: boolean,
  setOpenDialog: React.Dispatch<React.SetStateAction<boolean>>,
  isProcessing: boolean,
  linkedData: any,
  setLinkedData: React.Dispatch<React.SetStateAction<any>>,
  setIsPlanOfRecord: React.Dispatch<React.SetStateAction<boolean>>
): JSX.Element => {
  return (
    <ArgosDialog open={openDialog} onClose={() => { setOpenDialog(false); setLinkedData({}); }} width={'auto'} height={'auto'} headerTitle={'Plan of Record Adas Program Constraint'} headerTitleAlign={'left'}>
      <BoxWithCenteredContent horizontal vertical style={{ height: '100%', display: 'flex', flexFlow: 'column nowrap' }}>
      {openDialog && isProcessing && (
        <LoaderWrapper>
        <Spinner animation={'border'} style={{ color: 'grey' }} />
        <p style={{ width: '500px', textAlign: 'center' }}>
          Checking to see if this coverage analysis study is linked to one or more ADAS programs that are PoR
        </p>
        </LoaderWrapper>
      )}
      {linkedData && linkedData?.message && (
        <p style={{ width: '500px', textAlign: 'center' }}>
        {linkedData?.message}
        </p>
      )}
      {linkedData  && linkedData?.items?.length > 0  && (
        <p style={{ width: '500px', textAlign: 'center' }}>
          {linkedData?.items.map((item: any, index: number) => (
          <span key={index}>
            {item.programName}-{item.modelYear}
            {index < linkedData.items.length - 1 && ', '}
          </span>
          ))}
        </p>
      )}
      <ArgosFlexbox>
        {linkedData && linkedData?.items?.length === 0 && (
        <ArgosButton
          onClick={() => {
          setOpenDialog(false);
          setIsPlanOfRecord(false);
          setLinkedData({});
          }}
        >
          YES
        </ArgosButton>
        )}
        <ArgosButton onClick={() => {setOpenDialog(false);setLinkedData({});}}>{linkedData  && linkedData?.items?.length > 0 ? "OK" : "CANCEL"}</ArgosButton>
      </ArgosFlexbox>
      </BoxWithCenteredContent>
    </ArgosDialog>
  );
};

const EditStudyDialog = (props: any) => {

  const fetchLinkedHandle = usePostRequest(checkPlanOfRecordEndpoint)
  const fetchHandle = usePostRequest(usersDataEndpoint)
  const dispatch = useAppDispatch()
  const hasWriteAccess = useHasWriteAccess()
  const [openDialog, setOpenDialog] = useState<boolean>(false)
  const [linkedData, setLinkedData] = useState<Record<string, any>>({})
  const [userData, setUserData] = useState([])
  const [studyName, setStudyName] = useState("")
  const [studyDescription, setStudyDescription] = useState("")
  const [studyOwner, setStudyOwner] = useState<string>("")
  const [prevStudyName, setPrevStudyName] = useState("")
  const [prevStudyDescription, setPrevStudyDescription] = useState("")
  const [prevStudyOwner, setPrevStudyOwner] = useState<string>()
  const [isLoading, setIsLoading] = useState<boolean | null>(null)
  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const [studyId, setStudyId] = useState<string>()
  const [studyPlanOfRecord, setIsPlanOfRecord] = useState<boolean>(false)
  const [studyArchive, setIsStudyArchive] = useState<boolean>(false)
  const [prevStudyPlanOfRecord, setPrevStudyPlanOfRecord] = useState<boolean>()
  const [prevStudyArchive, setPrevStudyArchive] = useState<boolean>(false)
  const user = useAppSelector(selectCurrentUser)
  const isAdmin = getIsUserAdmin(user)
  interface StudyDetailsData {
    id: string
    name: string
    description: string
    owner: string
    isPlanOfRecord: boolean
    isStudyArchive: boolean
  }

  const onClickCancel = () => {
    dispatch(setIsEditStudyDialogOpen(false))
  }

  const onClickSave = () => {
    const dataToEdit: any = {}
    if(prevStudyName != studyName){
      dataToEdit["name"]= studyName
    }
    if(prevStudyDescription != studyDescription) {
      dataToEdit["description"] = studyDescription
    }
    if(prevStudyOwner != studyOwner){
      dataToEdit["owner"] = studyOwner
    }
    if (prevStudyPlanOfRecord != studyPlanOfRecord){
      dataToEdit["isPlanOfRecord"] = studyPlanOfRecord

    }
    if(prevStudyArchive != studyArchive){
        dataToEdit["isStudyArchive"] = studyArchive
    }
    const reqBody = {
      "data" : dataToEdit,
      "id": studyId,
    }
    if(Object.keys(dataToEdit).length != 0){
      props.editStudy(reqBody)
    }
    dispatch(setIsEditStudyDialogOpen(false))
  }

  function handleStudyOwnerChange(e: any) {
    setStudyOwner(e.target.value)
  }

  const fetchData = async () => {
    fetchHandle().then((userData: any) => {
      setUserData(userData.filter((element: IUser) => (
        getRouteKeyAccessLevel(EArgosRouteKeys.COVERAGE_REQUIREMENTS, element) === EUserPermissionAccess.READ_WRITE || element.permissions.admin
      )))
    }).catch(err => {
      console.log(err)
      toast.error("Failed to load user data")
      setIsLoading(false);
    })
    setIsLoading(false)
  }

  useEffect(() => {
    const studyDetails = (store.getState()?.globalDialog?.component?.params as StudyDetailsData)
    setStudyName(studyDetails.name)
    setPrevStudyName(studyDetails.name)
    setStudyDescription(studyDetails.description)
    setPrevStudyDescription(studyDetails.description)
    setStudyOwner(studyDetails.owner)
    setPrevStudyOwner(studyDetails.owner)
    setStudyId(studyDetails.id)
    setPrevStudyPlanOfRecord(studyDetails.isPlanOfRecord)
    setIsPlanOfRecord(studyDetails.isPlanOfRecord)
    setPrevStudyArchive(studyDetails.isStudyArchive)
    setIsStudyArchive(studyDetails.isStudyArchive)
  }, [])
  const gmid = useAppSelector(state => selectCurrentUser(state).id);
  useEffect(() => {
    setIsLoading(true)
    fetchData()
  }, [])

  return (
    <>
    {/* Dialog check for plan of record link to Adas */}
    {renderCheckAdasLinkDialog(
      openDialog,
      setOpenDialog,
      isProcessing,
      linkedData,
      setLinkedData,
      setIsPlanOfRecord
    )}
    <Container>
      <ArgosDialogHeader>
        <ArgosFlexbox style={{justifyContent: 'space-between', alignItems: 'center'}}>
          <Header>
            <div style={{
              color: 'white',
              paddingLeft: 5,
              fontSize: 14
            }}>Edit Study
            </div>
          </Header>
          <IconButton onClick={onClickCancel} sx={{color: 'white', padding: 0, fontSize: 'small'}}> 
            <CloseIcon />
          </IconButton>
        </ArgosFlexbox>
      </ArgosDialogHeader>
      {isLoading ?
        <LoaderWrapper>
          <Spinner animation={'border'} style={{color: 'grey'}}/>
        </LoaderWrapper>
        : hasWriteAccess && <div style={{
        padding: 5,
        alignSelf: 'center',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        alignItems: 'flex-start',
        gap: 20
      }}>
        <FormControlLabel
          control={<Checkbox
            onChange={(event, checked) => handleCheck(
              { target: { checked } },
              prevStudyPlanOfRecord!,
              setIsProcessing,
              setOpenDialog,
              fetchLinkedHandle,
              studyId,
              setLinkedData,
              setIsPlanOfRecord
            )}
            name="study_plan_of_record"
            checked={studyPlanOfRecord}
            id="outlined-basic"
            disabled={prevStudyArchive || planOfRecordDisablePermissions(hasWriteAccess, studyOwner, gmid)}
          />} label="Plan of Record"
        ></FormControlLabel>
        <FormControlLabel
          control={<Checkbox
            onChange={(e) => {
              setIsStudyArchive(e.target.checked);
            }}
            name="study_Archive"
            checked={studyArchive}
            id="outlined-basic"
            disabled={allowAdminOrOwnerPermissions(isAdmin, studyOwner, gmid)}
          />} label="Archive"
        ></FormControlLabel>
        <ArgosTextField
          required
          onChange={(e) => setStudyName(e.target.value)}
          value={studyName}
          name="study_name"
          sx={{width: fieldWidth, height: fieldHeight}}
          type="text"
          id="outlined-basic"
          label="Name"
          disabled={prevStudyArchive}
        />
        <ArgosTextField
          multiline
          required
          rows={4}
          onChange={(e) => setStudyDescription(e.target.value)}
          value={studyDescription}
          name="study_description"
          sx={{width: fieldWidth, height: 2 * fieldHeight}}
          type="text"
          id="outlined-basic"
          label="Description"
          disabled={prevStudyArchive}
        />
        <FormControl variant="standard" sx={{width: fieldWidth, height: fieldHeight}}>
          <InputLabel id="study_owner">Owner</InputLabel>
          <ArgosSelect
            disabled={prevStudyArchive}
            required
            label="Owner"
            type='text'
            value={studyOwner}
            id="study_owner"
            onChange={(e) => handleStudyOwnerChange(e)}
            sx={{width: fieldWidth, height: fieldHeight}}
          >
            {
              userData.sort((a: User, b: User) =>
                a?.lastName.localeCompare(b.lastName) ||
                a?.firstName.localeCompare(b.firstName)
              ).map((user: User) =>
                <ArgosMenuItem
                  value={user.id}
                >
                  {user?.firstName + " " + user?.lastName ?? user.id}
                </ArgosMenuItem>)
            }
          </ArgosSelect>
        </FormControl>
      </div>}
      <SeparatorLine></SeparatorLine>
      <div style={{alignSelf: 'center', display: 'flex', gap: 10, marginTop: 10}}>
        <ArgosButton
          onClick={onClickCancel}
        >
          Cancel
        </ArgosButton>
        <ArgosButton
          onClick={onClickSave}
          disabled={determineSaveState(studyArchive, prevStudyArchive) || studyName === "" || studyDescription === "" || studyOwner === "" || studyOwner === null}
        >
          Save
        </ArgosButton>
      </div>
    </Container>
    </>
  )
}

export default EditStudyDialog
